import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { previewVendorPurchaseOrders, vendorPoSendErrorMessage } from '../lib/vendorPurchaseOrderApi';
import { openPrintInNewTab } from '../lib/serverPrint';
import { formatDateTime as fmtDateTime } from "../utils/dateFormat";

interface SendPurchaseOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultEmail?: string;
  poNumber?: string;
  sending?: boolean;
  poId?: string;
  alreadySent?: boolean;
  sentAt?: string | null;
  /** D170.3: set on an amended PO (changed since it was sent) - the revision this sends. */
  amendedRevision?: number;
  onSend: (data: { recipients: string[]; subject: string; message: string; format: 'pdf' | 'excel' }) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SendPurchaseOrderModal: React.FC<SendPurchaseOrderModalProps> = ({
  isOpen,
  onClose,
  defaultEmail,
  poNumber,
  sending,
  poId,
  alreadySent,
  sentAt,
  amendedRevision,
  onSend
}) => {
  const [recipients, setRecipients] = useState<string[]>([]);
  const [recipientInput, setRecipientInput] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [format, setFormat] = useState<'pdf' | 'excel'>('pdf');
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const prefill =
      defaultEmail && defaultEmail !== '-' && EMAIL_RE.test(defaultEmail) ? [defaultEmail] : [];
    setRecipients(prefill);
    setRecipientInput('');
    setSubject(`Purchase Order ${poNumber || ''} — United Ship Chandlers`);
    setMessage('');
    setFormat('pdf');
  }, [isOpen, defaultEmail, poNumber]);

  /** Add the typed address(es) — comma/semicolon/space separated — to the recipient list. */
  const addRecipients = (): boolean => {
    const parts = recipientInput
      .split(/[,;\s]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (!parts.length) return true;
    const invalid = parts.filter((p) => !EMAIL_RE.test(p));
    if (invalid.length) {
      alert(`Invalid email address: ${invalid.join(', ')}`);
      return false;
    }
    setRecipients((prev) => prev.concat(parts.filter((p) => !prev.includes(p))));
    setRecipientInput('');
    return true;
  };

  const removeRecipient = (email: string) => {
    setRecipients((prev) => prev.filter((r) => r !== email));
  };

  const handleSubmit = () => {
    // Fold any address still sitting in the input into the list first
    const pending = recipientInput.trim();
    let finalRecipients = recipients;
    if (pending) {
      const parts = pending.split(/[,;\s]+/).map((p) => p.trim()).filter(Boolean);
      const invalid = parts.filter((p) => !EMAIL_RE.test(p));
      if (invalid.length) {
        alert(`Invalid email address: ${invalid.join(', ')}`);
        return;
      }
      finalRecipients = finalRecipients.concat(parts.filter((p) => !finalRecipients.includes(p)));
    }
    if (finalRecipients.length === 0) {
      alert('Please add at least one recipient email address');
      return;
    }
    if (!subject.trim()) {
      alert('Please enter a subject');
      return;
    }

    onSend({ recipients: finalRecipients, subject: subject.trim(), message, format });
  };

  if (!isOpen) return null;

  const content = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 modal-overlay-safe">
      {/* Modal Container */}
      <div className="bg-white rounded-lg relative w-full max-w-[610px] max-h-[90vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 pb-4">
          <h2 className="text-base font-semibold leading-6 text-slate-900">
            Send Purchase Order
          </h2>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center bg-[#D9D9D9] rounded hover:bg-gray-400 transition-colors"
          >
            <span className="text-slate-400 text-lg leading-none">×</span>
          </button>
        </div>

        {/* Form Fields */}
        <div className="flex-1 px-6 pb-4">
          {alreadySent && (
            <p
              className="mb-4 text-xs text-slate-500"
              style={{ fontFamily: 'Public Sans' }}
            >
              {`Sent${sentAt ? ` on ${fmtDateTime(sentAt)}` : ''} — sending again will resend.`}
            </p>
          )}
          {amendedRevision != null && (
            <p className="mb-4 text-xs text-amber-700" style={{ fontFamily: 'Public Sans' }}>
              {`Changed since it was sent: this sends Revision ${amendedRevision} in place of the copy sent earlier.`}
            </p>
          )}
          {/* Recipients */}
          <div className="mb-4">
            <label
              className="block mb-2 text-slate-900"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px'
              }}
            >
              Recipients <span className="text-red-500">*</span>
            </label>
            {recipients.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {recipients.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 text-blue-800 rounded text-sm"
                    style={{ fontFamily: 'Public Sans' }}
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeRecipient(email)}
                      className="text-blue-500 hover:text-blue-800 leading-none"
                      aria-label={`Remove ${email}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={recipientInput}
                onChange={(e) => setRecipientInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addRecipients();
                  }
                }}
                onBlur={() => addRecipients()}
                placeholder="vendor@supplier.com, purchasing@company.com"
                className="flex-1 px-3 py-2 border border-[#D7DBD9] rounded-md bg-white placeholder-[#999999] text-slate-900"
                style={{
                  fontFamily: 'Public Sans',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '24px'
                }}
              />
              <button
                type="button"
                onClick={addRecipients}
                className="px-4 py-2 border border-primary-500 text-primary-500 rounded-md text-sm font-semibold hover:bg-blue-50"
                style={{ fontFamily: 'Public Sans' }}
              >
                Add
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500" style={{ fontFamily: 'Public Sans' }}>
              Add multiple addresses — separate with commas or press Enter.
            </p>
          </div>

          {/* Subject */}
          <div className="mb-4">
            <label
              className="block mb-2 text-slate-900"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px'
              }}
            >
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter Subject"
              className="w-full px-3 py-2 border border-[#D7DBD9] rounded-md bg-white placeholder-[#999999] text-slate-900"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px'
              }}
            />
          </div>

          {/* Message */}
          <div className="mb-4">
            <label
              className="block mb-2 text-slate-900"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px'
              }}
            >
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write here..."
              rows={6}
              className="w-full px-3 py-2 border border-[#D7DBD9] rounded-md bg-white placeholder-[#999999] text-slate-900 resize-none"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px'
              }}
            />
          </div>

          {/* Format */}
          <div className="mb-4">
            <label
              className="block mb-2 text-slate-900"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px'
              }}
            >
              Format
            </label>
            <div className="flex gap-6">
              <label className="inline-flex items-center gap-2 text-slate-900" style={{ fontFamily: 'Public Sans', fontSize: '14px' }}>
                <input
                  type="radio"
                  name="po-format"
                  value="pdf"
                  checked={format === 'pdf'}
                  onChange={() => setFormat('pdf')}
                />
                PDF
              </label>
              <label className="inline-flex items-center gap-2 text-slate-900" style={{ fontFamily: 'Public Sans', fontSize: '14px' }}>
                <input
                  type="radio"
                  name="po-format"
                  value="excel"
                  checked={format === 'excel'}
                  onChange={() => setFormat('excel')}
                />
                Excel
              </label>
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="p-6 pt-0 flex gap-3">
          <button
            type="button"
            disabled={!poId || previewing}
            onClick={async () => {
              if (!poId) return;
              setPreviewing(true);
              // D165: the tab opens inside the click; the server print takes a few seconds.
              await openPrintInNewTab(() => previewVendorPurchaseOrders([poId], format), {
                onBlocked: () => window.alert('Allow pop-ups for this site, then press Preview again.'),
                onError: (e) => window.alert(vendorPoSendErrorMessage(e, 'Failed to generate preview')),
              });
              setPreviewing(false);
            }}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-md border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-60"
          >
            {previewing ? 'Generating…' : 'Preview document'}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="flex-1 bg-white border border-[#D7DBD9] rounded-lg flex items-center justify-center hover:bg-slate-50 transition-colors h-10 disabled:opacity-50"
          >
            <span
              className="text-slate-900"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: '24px'
              }}
            >
              Cancel
            </span>
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={sending}
            className="flex-1 bg-primary-500 rounded-lg flex items-center justify-center hover:bg-primary-600 transition-colors h-10 disabled:opacity-60"
          >
            <span
              className="text-white"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: '24px'
              }}
            >
              {sending ? 'Sending…' : amendedRevision != null ? 'Send amendment' : alreadySent ? 'Resend' : 'Send'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default SendPurchaseOrderModal;
